// ============================================================
// پایگاه دانش محلی — لایه‌ی دوم پاسخ
//
// این لایه بدون شبکه و بدون هوش مصنوعی جواب می‌دهد. دو منبع دارد:
//   ۱) KNOWLEDGE — نوشته‌شده با دست، برای سوال‌های پرتکرار سامانه
//   ۲) buildSiteIndex() — ساخته‌شده خودکار از همان صفحات و
//      مجموعه‌داده‌هایی که کاربر با نقش خودش حق دیدنشان را دارد
//
// یعنی وقتی صفحه یا مجموعه‌داده‌ی جدیدی به سامانه اضافه شود، دستیار
// بدون تغییر این فایل جوابش را می‌داند.
// ============================================================

import { ROLE_LABELS, SITE_PAGES, canAccess } from "./access";
import { DATASETS } from "./datasets";
import { normalize } from "./text";

export interface KnowledgeEntry {
    /** هر تگی که در جمله پیدا شود، weight را به امتیاز اضافه می‌کند */
    tags?: string[];
    /**
     * هر گروه باید دست‌کم یک عضوش در جمله باشد، ولی لازم نیست کنار
     * هم باشند. در فارسی ذرات وسط عبارت می‌آیند («تخلف رو با چه
     * معیاری حساب می‌کنید») و تطبیق عبارتی اینجا شکست می‌خورد.
     */
    allOf?: string[][];
    weight: number;
    answer: string;
    /** اگر پر باشد، فقط برای این نقش‌ها جواب داده می‌شود */
    roles?: number[];
}

export const KNOWLEDGE: KnowledgeEntry[] = [
    {
        tags: ["این سامانه چیه", "هاویر", "رصد", "سامانه چیکار", "اینجا کجاست", "درباره سامانه"],
        weight: 6,
        answer:
            "این سامانه‌ی رصد توزیع آرد است: از صدور حواله برای نانوا تا تخلیه توسط راننده، " +
            "شمارش خودکار (ناظر هوش مصنوعی) و ثبت نهایی بازرس شهرستان. هر مرحله رکورد و " +
            "تصویر خودش را دارد و در گزارش‌ها با هم مقایسه می‌شوند.",
    },
    {
        allOf: [
            ["تخلف", "تخلفات", "خارج از شبکه", "کم پزی"],
            ["چیه", "چی هست", "یعنی چی", "معنی", "چطور", "چگونه", "معیار", "حساب"],
        ],
        weight: 9,
        answer:
            "در گزارش تخلفات دو شاخص محاسبه می‌شود:\n" +
            "• «خارج از شبکه»: اختلاف بین مقداری که راننده تخلیه کرده و مقداری که بازرس شمرده است.\n" +
            "• «کم‌پزی»: اختلاف بین سهمیه/مصرف مجاز و مقدار واقعی مصرف‌شده در بازه‌ی زمانی.\n" +
            "فاصله‌ی روزهای بین تخلیه و بازرسی هم در ستون «فاصله زمانی» می‌آید.",
    },
    {
        allOf: [
            ["حواله"],
            ["چیه", "چی هست", "یعنی چی", "معنی", "تفاوت", "فرق"],
        ],
        weight: 8,
        answer:
            "حواله، مجوز تحویل آرد به یک نانوا در یک دوره و مرحله‌ی توزیع مشخص است. " +
            "هر حواله «کیسه حواله» دارد؛ بعد راننده تخلیه می‌کند («تخلیه راننده») و " +
            "بازرس عدد نهایی را ثبت می‌کند («شمارش بازرس»). اختلاف این سه عدد، پایه‌ی گزارش‌هاست.",
    },
    {
        allOf: [
            ["دهه", "دوره", "مرحله"],
            ["چیه", "یعنی چی", "معنی", "تفاوت", "فرق", "چند"],
        ],
        weight: 8,
        answer:
            "توزیع آرد در «دوره»ها و هر دوره در «مرحله»ها انجام می‌شود. گزارش دهه، همان " +
            "گزارش توزیع است که به جای بازه‌ی تاریخ، بر اساس کد دوره و کد مرحله گرفته می‌شود. " +
            "فهرست دوره‌ها و مراحل از سرور می‌آید و مراحل به دوره‌ی انتخاب‌شده وابسته‌اند.",
    },
    {
        allOf: [
            ["سهمیه"],
            ["چیه", "یعنی چی", "معنی", "از کجا", "چطور", "بارگذاری", "آپلود", "اکسل"],
        ],
        weight: 8,
        answer:
            "سهمیه‌ی هر خبازی از فایل اکسل بارگذاری می‌شود (صفحه‌ی «بارگذاری سهمیه خبازی‌ها»، " +
            "فقط برای ادمین) و در «سهمیه خبازی‌ها» قابل جست‌وجو است. هر رکورد شامل کد خریدار، " +
            "نوع پخت، دوره‌ی توزیع و مقدار سهمیه است.",
    },
    {
        allOf: [
            ["روز تعطیل", "تعطیلی", "روز off"],
            ["چیه", "چرا", "یعنی چی", "کجا", "چطور", "تاثیر", "اثر"],
        ],
        weight: 8,
        answer:
            "روز تعطیل در پروفایل خبازی ثبت می‌شود (صفحه‌ی اطلاعات خبازی‌ها) و در وظایف بازرس " +
            "اثر می‌گذارد. نکته‌ی فنی: مقدار باید با فاصله‌ی معمولی ذخیره شود («سه شنبه»، نه با " +
            "نیم‌فاصله)، وگرنه در وظایف بازرس بی‌اثر می‌ماند.",
    },
    {
        allOf: [
            ["مهلت", "ددلاین", "چند ساعت", "چند روز"],
            ["تخلیه", "بازرسی", "راننده", "بازرس", "ثبت"],
        ],
        weight: 8,
        roles: [0],
        answer:
            "دو تنظیم در «تنظیمات تخلیه و بازرسی» هست: مهلت ثبت تخلیه توسط راننده (ساعت) و " +
            "حداقل فاصله‌ی تخلیه تا بازرسی (ساعت). هر دو فقط برای سوپر ادمین قابل تغییرند.",
    },
    {
        allOf: [
            ["اکسل", "excel", "خروجی"],
            ["بگیر", "بگیرم", "چطور", "چجوری", "دانلود", "میشه"],
        ],
        weight: 7,
        answer:
            "در بالای هر فهرست (حواله‌ها، تخلفات، سهمیه، وظایف) دکمه‌ی خروجی اکسل هست و همان " +
            "فیلترهای فعال را اعمال می‌کند. من در پنجره‌ی گفتگو فقط رکوردها را نشان می‌دهم؛ " +
            "برای فایل اکسل بگویید «منو ببر به» همان صفحه.",
    },
    {
        allOf: [
            ["تصویر", "عکس", "عکس ها", "تصاویر"],
            ["کجا", "چطور", "ببینم", "هست", "چند"],
        ],
        weight: 6,
        answer:
            "تصاویر به رکورد حواله می‌چسبند و دو دسته‌اند: تصاویر راننده (هنگام تخلیه) و " +
            "تصاویر بازرس (هنگام بازرسی). در صفحه‌ی حواله‌ها و وظایف، با دکمه‌ی گالری باز می‌شوند. " +
            "در پنجره‌ی گفتگو تصویر نشان نمی‌دهم؛ برای دیدنشان به همان صفحه بروید.",
    },
    {
        allOf: [
            ["موقعیت", "لوکیشن", "نقشه", "مکان"],
            ["چیه", "کجا", "چطور", "ثبت", "نشد", "خالی", "نداره"],
        ],
        weight: 7,
        answer:
            "موقعیت خبازی در پروفایل خودش ثبت می‌شود و در وظایف بازرس از همان پروفایل خوانده " +
            "می‌شود (نه موقعیت لحظه‌ای راننده). اگر طول و عرض جغرافیایی هر دو خالی باشند، یعنی " +
            "موقعیت ثبت نشده و هیچ مقدار جایگزینی نباید جایش گذاشته شود.",
    },
    {
        tags: ["نقش ها", "نقش های سامانه", "سطوح دسترسی", "role"],
        weight: 5,
        answer:
            "نقش‌های سامانه: " +
            Object.keys(ROLE_LABELS)
                .map((k) => ROLE_LABELS[Number(k)])
                .join("، ") +
            ". هر نقش فقط صفحات و داده‌های خودش را می‌بیند و من هم همین محدودیت را رعایت می‌کنم.",
    },
    {
        allOf: [
            ["رمز", "پسورد", "password"],
            ["عوض", "تغییر", "فراموش", "چطور"],
        ],
        weight: 7,
        answer:
            "تغییر رمز از صفحه‌ی پروفایل کاربر انجام می‌شود. اگر رمز را فراموش کرده‌اید، " +
            "ادمین سامانه می‌تواند از «مدیریت کاربران» آن را بازنشانی کند.",
    },
    {
        allOf: [
            ["خطا", "ارور", "error", "۴۰۱", "401", "بیرون انداخت"],
            ["چرا", "چیه", "میده", "گرفتم", "شد"],
        ],
        weight: 6,
        answer:
            "خطای ۴۰۱ یعنی نشست منقضی شده؛ سامانه یک بار خودش با رفرش‌توکن تلاش می‌کند و اگر " +
            "نشد به صفحه‌ی ورود برمی‌گردید. خطای ۴۰۳ یعنی داده هست ولی نقش شما اجازه‌ی دیدنش " +
            "را ندارد.",
    },
];

// ---------- ورودی‌های ساخته‌شده‌ی خودکار ----------

/**
 * از صفحات و مجموعه‌داده‌های واقعی سامانه، ورودی دانش می‌سازد.
 * فقط چیزهایی که نقش کاربر حق دیدنشان را دارد.
 */
export const buildSiteIndex = (role: number | null): KnowledgeEntry[] => {
    const entries: KnowledgeEntry[] = [];

    SITE_PAGES.forEach((page) => {
        if (!canAccess(role, page.roles)) return;
        entries.push({
            tags: page.tags.concat([page.title]),
            weight: 3,
            answer:
                "«" + page.title + "» در دسترس شماست. برای رفتن به آن بگویید «منو ببر به " +
                page.title + "»" +
                (DATASETS.filter((d) => d.link === page.link).length
                    ? "، یا همین‌جا از من داده‌اش را بخواهید."
                    : "."),
        });
    });

    DATASETS.forEach((dataset) => {
        if (!canAccess(role, dataset.roles)) return;
        const asks = dataset.slots
            .filter((s) => !s.optional && !s.defaultValue)
            .map((s) => s.label);
        entries.push({
            tags: dataset.match[0],
            weight: 2,
            answer:
                "برای «" + dataset.title + "» می‌توانم رکوردها را همین‌جا نشان بدهم" +
                (asks.length ? "؛ فقط " + asks.join(" و ") + " را لازم دارم." : "."),
        });
    });

    return entries;
};

export interface OfflineAnswer {
    answer: string;
    score: number;
}

/**
 * امتیازدهی: هر تگ منطبق، weight را اضافه می‌کند. در allOf هر گروه
 * باید دست‌کم یک عضوش حاضر باشد، وگرنه کل ورودی رد می‌شود.
 * بیشترین امتیاز برنده است.
 */
export const findOfflineAnswer = (message: string, role: number | null): OfflineAnswer | null => {
    const n = normalize(message);
    if (!n) return null;

    const pool = KNOWLEDGE.filter((e) => !e.roles || canAccess(role, e.roles)).concat(
        buildSiteIndex(role)
    );

    let best: OfflineAnswer | null = null;

    for (let i = 0; i < pool.length; i++) {
        const entry = pool[i];
        let score = 0;

        if (entry.allOf) {
            let allGroupsHit = true;
            for (let g = 0; g < entry.allOf.length; g++) {
                const group = entry.allOf[g];
                let hit = false;
                for (let t = 0; t < group.length; t++) {
                    if (n.indexOf(normalize(group[t])) !== -1) {
                        hit = true;
                        break;
                    }
                }
                if (!hit) {
                    allGroupsHit = false;
                    break;
                }
            }
            if (allGroupsHit) score += entry.weight;
        }

        if (entry.tags) {
            for (let t = 0; t < entry.tags.length; t++) {
                if (n.indexOf(normalize(entry.tags[t])) !== -1) score += entry.weight;
            }
        }

        if (score > 0 && (!best || score > best.score)) {
            best = { answer: entry.answer, score };
        }
    }

    return best;
};
